import type { FC, ReactNode } from 'react'
import type { LevelIcon } from '@/constants/content'
import type { PixelPalette } from '@/lib/pixel'

import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'
import { useInView } from '@/hooks/use-in-view'
import { PixelSprite } from '@/components/pixel/pixel-sprite'
import { PixelArrow } from '@/components/generals/pixel-button'

import PixelEdge from '@/components/pixel/pixel-edge'

import { LEVELS } from '@/constants/content'
import {
    ICON_CHECK,
    ICON_CROWN,
    ICON_EGG,
    ICON_FOOTPRINT,
    ICON_HATCH,
    ICON_LOCK,
    ICON_PALETTE
} from '@/constants/sprites'

const LEVEL_ICONS: Record<LevelIcon, ReadonlyArray<string>> = {
    egg: ICON_EGG,
    hatch: ICON_HATCH,
    footprint: ICON_FOOTPRINT,
    crown: ICON_CROWN
}

const LEVEL_MOTION: Record<LevelIcon, string> = {
    egg: 'animate-wobble origin-bottom',
    hatch: 'animate-wobble origin-bottom [--wobble-duration:1.5s]',
    footprint: 'animate-hop',
    crown: 'animate-float'
}

const LOCKED_PALETTE: PixelPalette = {
    K: '#6b6b6b',
    w: '#2b2b2b',
    W: '#2b2b2b',
    G: '#3d3d3d',
    P: '#3d3d3d'
}

const FERN_PALETTE: PixelPalette = { K: 'var(--fern)' }

const MUTED_PALETTE: PixelPalette = { K: '#8a8a8a' }

const XP_BLOCKS = 24

const ProcessSection: FC = (): ReactNode => {
    const [ref, inView] = useInView({ threshold: 0.3 })
    const [unlocked, setUnlocked] = useState(0)

    useEffect(() => {
        if (!inView) return

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            setUnlocked(LEVELS.length)
            return
        }

        let current = 0

        const timer = window.setInterval(() => {
            current += 1
            setUnlocked(current)

            if (current >= LEVELS.length) window.clearInterval(timer)
        }, 650)

        return () => window.clearInterval(timer)
    }, [inView])

    const filled = Math.round((unlocked / LEVELS.length) * XP_BLOCKS)
    const complete = unlocked >= LEVELS.length

    return (
        <section
            id='process'
            aria-labelledby='process-title'
            className='relative mt-16 bg-ink py-24 text-paper md:mt-24 md:py-32'
        >
            <PixelEdge color='var(--ink)' seed={5} className='absolute inset-x-0 bottom-[calc(100%-1px)]' />

            <div className='mx-auto max-w-7xl px-5 md:px-8'>
                <p className='font-heading text-lg text-bubblegum md:text-xl'>
                    how we work
                </p>

                <h2 id='process-title' className='mt-2 font-heading text-[clamp(2.6rem,7.5vw,6rem)] leading-[0.9]'>
                    from egg to <span className='text-fern'>t-rex</span>
                </h2>

                <p className='mt-5 max-w-xl text-base leading-relaxed font-medium text-paper/80 md:text-lg'>
                    Every project levels up the same way, one stage at a time. No skipped steps, no mystery boxes.
                </p>

                <ol ref={ref} className='mt-14 grid gap-6 md:mt-16 md:grid-cols-2 lg:grid-cols-4'>
                    {LEVELS.map((level, index) => {
                        const isUnlocked = index < unlocked

                        return (
                            <li key={level.level}>
                                <article
                                    className={cn(
                                        'h-full border-2 p-6 transition-[background-color,border-color] duration-300 ease-[steps(3,end)]',
                                        isUnlocked ? 'border-bubblegum bg-[#1f1f1f]' : 'border-paper/20 bg-transparent'
                                    )}
                                >
                                    <div className='flex items-center justify-between gap-3'>
                                        <span className='font-heading text-sm text-paper/70'>
                                            level {level.level}
                                        </span>

                                        <span
                                            aria-hidden='true'
                                            className={cn(
                                                'flex items-center gap-1.5 border-2 px-2 py-0.5 font-heading text-xs',
                                                isUnlocked ? 'border-fern text-fern' : 'border-paper/25 text-paper/50'
                                            )}
                                        >
                                            <PixelSprite
                                                rows={isUnlocked ? ICON_CHECK : ICON_LOCK}
                                                palette={isUnlocked ? FERN_PALETTE : MUTED_PALETTE}
                                                className='h-3 w-auto'
                                            />
                                            {isUnlocked ? "cleared" : "locked"}
                                        </span>
                                    </div>

                                    <div
                                        aria-hidden='true'
                                        className={cn(
                                            'mt-6 grid size-24 place-items-center border-2 transition-colors duration-300 ease-[steps(3,end)]',
                                            isUnlocked ? 'animate-pop-in border-paper bg-bubblegum' : 'border-paper/20 bg-paper/5'
                                        )}
                                    >
                                        <PixelSprite
                                            rows={LEVEL_ICONS[level.icon]}
                                            palette={isUnlocked ? ICON_PALETTE : LOCKED_PALETTE}
                                            className={cn('h-14 w-auto', isUnlocked && LEVEL_MOTION[level.icon])}
                                        />
                                    </div>

                                    <p className='mt-6 font-heading text-sm text-bubblegum'>
                                        stage: {level.stage}
                                    </p>

                                    <h3 className='mt-1 font-heading text-3xl leading-none'>
                                        {level.title}
                                    </h3>

                                    <p className='mt-3 text-sm leading-relaxed text-paper/80 md:text-[15px]'>
                                        {level.text}
                                    </p>
                                </article>
                            </li>
                        )
                    })}
                </ol>

                <div className='mt-12 flex flex-col gap-6 md:flex-row md:items-center md:gap-10'>
                    <div aria-hidden='true' className='flex flex-1 items-center gap-4'>
                        <span className='font-heading text-sm'>xp</span>

                        <div className='flex flex-1 gap-1'>
                            {Array.from({ length: XP_BLOCKS }, (_, block) => (
                                <span
                                    key={block}
                                    className={cn('h-4 flex-1 transition-colors duration-200', block < filled ? 'bg-fern' : 'bg-paper/15')}
                                />
                            ))}
                        </div>

                        <span className='font-heading text-sm tabular-nums'>
                            {unlocked}/{LEVELS.length}
                        </span>
                    </div>

                    <a
                        href='#contacts'
                        className={cn(
                            'group/button inline-flex items-center gap-2 self-start font-heading text-lg text-bubblegum underline decoration-2 underline-offset-[6px] transition-opacity duration-300 hover:decoration-paper md:self-auto',
                            complete ? 'opacity-100' : 'opacity-60'
                        )}
                    >
                        {complete ? "level complete! start yours" : "start your level 01"}
                        <PixelArrow />
                    </a>
                </div>
            </div>

            <PixelEdge color='var(--ink)' seed={9} flip className='absolute inset-x-0 top-[calc(100%-1px)]' />
        </section>
    )
}

export default ProcessSection
